import {
    OnInit,
    Component,
    Input,
    Optional,
    Inject,
    Output,
    EventEmitter,
} from "@angular/core";
import { SupplierDataDetailsViewComponent } from "../../../../modules/booking/supplier-data-details/supplier-data-details.view.component";
import { IntlService, CldrIntlService } from "@progress/kendo-angular-intl";
import { TranslationsProvider } from "../../../../core/translations.provider";
import { CustomAuthorizationService } from "../../../../core/auth/custom-authorization.service";
import { HttpClient } from "@angular/common/http";

const ServiceTypes = require("../service-types.json");

// Service templates
const ACCO = "ACCO";
const VERV = "VERV";
const ODNS = "ODNS";
const OTHER = "OTHER";

const DEFAULT_SERVICE_ICON = "fa-address-book-o";

@Component({
    selector: "djiv-booking-detail-table",
    templateUrl: "./djiv-booking-detail-table.component.html",
    styleUrls: ["./djiv-booking-detail-table.component.css"],
})
export class DJIVBookingDetailTableComponent {
    @Input() BookingData;
    @Input() isLoading: boolean;
    @Input() isReadonly: boolean;
    @Input() allowOnRequestDecision: boolean;
    @Output() onApproveRequest = new EventEmitter<{
        item: any;
    }>();

    @Output() onOpenDeclineRequest = new EventEmitter<{
        selectedSegment: any;
    }>();

    public displayUnitReference: boolean = false;

    constructor(
        private http: HttpClient,
        @Optional() public parent: SupplierDataDetailsViewComponent,
        @Inject(IntlService) protected intl: IntlService,
        @Inject(CustomAuthorizationService)
        protected customAuthorizationService: CustomAuthorizationService,
        private translationsProvider: TranslationsProvider,
    ) {
        Promise.all([
            this.customAuthorizationService.processAuthorizationResponse(),
        ]).then(() => {
            this.displayUnitReference =
                this.customAuthorizationService.displayUnitReference;
        });

        const culture = this.translationsProvider.getCulture() || "en-GB";
        (this.intl as CldrIntlService).localeId = culture;

        this.translationsProvider.localeChanges.subscribe((locale) => {
            (this.intl as CldrIntlService).localeId = locale;
        });
    }

    confirmBooking(bookingSegmentId: string, externalBookRef, unitReference) {
        this.parent.confirmBooking(
            bookingSegmentId,
            externalBookRef,
            unitReference,
        );
    }

    public getServiceTitle(bookingSegment): string {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case ACCO:
                return bookingSegment.acco.RoomDescription;
            case VERV:
                return bookingSegment.verv.TransportDescription;
            case ODNS:
                return bookingSegment.odns.ExtraDescription;
            default:
                return "";
        }
    }

    public getServiceExtraType(bookingSegment): string {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case ACCO:
                return bookingSegment.acco.BoardType;
            default:
                return "";
        }
    }

    public isServiceLocationBased(bookingSegment): boolean {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case VERV:
            case ODNS:
                return true;
            default:
                return false;
        }
    }

    public getStartLocationTranslation(bookingSegment): string {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case VERV:
                return "modules.Booking.views.SupplierDataDetails.hopOnLocation";
            case ODNS:
                return "modules.Booking.views.SupplierDataDetails.pickupLocation";
            default:
                return "";
        }
    }

    public getEndLocationTranslation(bookingSegment): string {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case VERV:
                return "modules.Booking.views.SupplierDataDetails.hopOffLocation";
            case ODNS:
                return "modules.Booking.views.SupplierDataDetails.dropoffLocation";
            default:
                return "";
        }
    }

    public getStartLocation(bookingSegment): string {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case VERV:
                return bookingSegment.verv.HopOnLocation;
            case ODNS:
                return bookingSegment.odns.PickupLocation;
            default:
                return "";
        }
    }

    public getEndLocation(bookingSegment): string {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case VERV:
                return bookingSegment.verv.HopOffLocation;
            case ODNS:
                return bookingSegment.odns.DropoffLocation;
            default:
                return "";
        }
    }

    public isTransport(bookingSegment): boolean {
        return this.getServiceTypeTemplate(bookingSegment) === VERV;
    }

    public isServiceTransfer(bookingSegment): boolean {
        return this.getServiceTypeTemplate(bookingSegment) === ODNS;
    }

    public isServiceAccomodation(bookingSegment): boolean {
        return this.getServiceTypeTemplate(bookingSegment) === ACCO;
    }

    public isServiceBusFerryTrain(bookingSegment): boolean {
        switch (bookingSegment.ServiceType) {
            case "BU":
            case "FE":
            case "TR":
                return true;
            case "EX":
                if (bookingSegment.odns.CategoryType === "TR") return true;
                else return false;
            default:
                return false;
        }
    }

    public getStartLocationOut(bookingSegment): string {
        return bookingSegment.odns.PickupLocationOut;
    }
    public getEndLocationOut(bookingSegment): string {
        return bookingSegment.odns.DropoffLocationOut;
    }

    public isServiceDurationBased(bookingSegment): boolean {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case ACCO:
                return true;
            default:
                return false;
        }
    }

    public getDurationTranslation(bookingSegment): string {
        switch (this.getServiceTypeTemplate(bookingSegment)) {
            case ACCO:
                return "modules.Booking.views.SupplierDataDetails.durationNights";
            case VERV:
            case ODNS:
                return "modules.Booking.views.SupplierDataDetails.durationDays";
            // default:
            //     return 'modules.Booking.views.SupplierData.columns.Duration';
        }
    }

    public getPreferenceDescription(description: string) {
        return description.includes(":")
            ? description.split(":")[1]
            : description;
    }

    public getPreferencePrefixDescription(description: string) {
        return description.includes(":") ? description.split(":")[0] : "";
    }

    public getServiceIconClass(bookingSegment): string {
        return this.getServiceTypeConfig(bookingSegment).icon
            ? `fa ${this.getServiceTypeConfig(bookingSegment).icon}`
            : "material-icons";
    }

    public getServiceIconText(bookingSegment): string {
        return this.getServiceTypeConfig(bookingSegment).materialIcon || "";
    }

    public getServiceTypeTemplate(bookingSegment): string {
        return this.getServiceTypeConfig(bookingSegment).template;
    }

    public isServiceCarRental(bookingSegment): boolean {
        return (
            bookingSegment.ServiceType === "AH" ||
            (bookingSegment.ServiceType === "EX" &&
                bookingSegment.odns.CategoryType === "AH")
        );
    }

    public getConfirmButtonTranslation(supplierConfirmed: string): string {
        let translation: string = "modules.Booking.views.SupplierDataDetails";

        switch (supplierConfirmed) {
            case "NotConfirmed": {
                translation += ".confirmBookingSegment";
                break;
            }

            case "Confirmed": {
                translation += ".confirmedBookingSegment";
                break;
            }

            case "ConfirmedButChanged": {
                translation += ".confirmedButChangedBookingSegment";
                break;
            }

            case "ApprovalProcessing": {
                translation += ".confirmedBookingSegment";
                break;
            }
        }

        return translation;
    }
    private getServiceTypeConfig(bookingSegment): {
        template: string;
        icon?: string;
        materialIcon?: string;
    } {
        return (
            (bookingSegment.ServiceType === "EX"
                ? ServiceTypes[bookingSegment.ServiceType][
                      bookingSegment.odns.CategoryType
                  ]
                : ServiceTypes[bookingSegment.ServiceType]) || {
                template: OTHER,
                icon: DEFAULT_SERVICE_ICON,
            }
        );
    }

    public approveRequest(item: any) {
        this.onApproveRequest.emit({ item: item });
    }

    public openDeclineRequest(item: any) {
        this.onOpenDeclineRequest.emit({
            selectedSegment: item,
        });
    }
}



// WEBPACK FOOTER //
// C:/Repositories/wfa3/SupplierPortal/app/src/app/shared/components/djiv-tables/djiv-booking-detail-table/djiv-booking-detail-table.component.ts